import { buttonV2Class } from '@synoptic/ui-kit/button-v2/button-v2.css.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.js';
import { LogoutIcon } from '@synoptic/ui-kit/icons/react/logout.js';
import { NavArrowDownIcon } from '@synoptic/ui-kit/icons/react/nav-arrow-down.js';
import { SettingsIcon } from '@synoptic/ui-kit/icons/react/settings.js';
import { UserCircleIcon } from '@synoptic/ui-kit/icons/react/user-circle.js';
import { UserIcon } from '@synoptic/ui-kit/icons/react/user.js';
import { useState } from 'react';
import { useLocation } from 'react-router';
import { LogoutButton } from '@/components/logout-button.tsx';
import { MyAvatar } from '@/components/my-avatar.tsx';
import { getRedirectLink } from '@/routes-utils/get-redirect-link.ts';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { useAppLoader } from '../loader.ts';
import { useAuthInfo } from '../use-auth-info.ts';
import { NavButtonItemV2, NavLinkItemV2, NavListV2 } from './nav-item-v2.tsx';
import { useNavVariantV2 } from './nav-variant-v2.ts';
import {
  guestActions,
  profileActions,
  profileAvatar,
  profileCard,
  profileChevron,
  profileDivider,
  profileHandle,
  profileName,
  profileText,
  profileTrigger,
} from './profile-menu-v2.css.ts';

const GuestProfileMenuV2 = () => {
  const { pathname } = useLocation();
  const rail = useNavVariantV2() === 'rail';
  const loginTo = getRedirectLink(paths.login, pathname);

  if (rail) {
    return (
      <NavLinkItemV2 to={loginTo} icon={<UserCircleIcon />} label="Log in" />
    );
  }

  return (
    <div className={guestActions}>
      <Link
        to={getRedirectLink(paths.signup, pathname)}
        className={buttonV2Class.primary}
      >
        Sign up
      </Link>
      <Link to={loginTo} className={buttonV2Class.secondary}>
        Log in
      </Link>
    </div>
  );
};

export const ProfileActionsV2 = () => {
  const { username } = useAuthInfo();

  return (
    <NavListV2 className={profileActions}>
      <NavLinkItemV2
        to={paths.makeUser(username)}
        icon={<UserIcon />}
        label="Profile"
      />
      <NavLinkItemV2
        to={paths.settings}
        icon={<SettingsIcon />}
        label="Settings"
      />
      <LogoutButton
        renderTrigger={({ loading, logout }) => (
          <NavButtonItemV2
            icon={<LogoutIcon />}
            label="Log out"
            disabled={loading}
            onClick={logout}
          />
        )}
      />
    </NavListV2>
  );
};

const AuthenticatedProfileMenuV2 = () => {
  const [open, setOpen] = useState(false);
  const { fullName, username } = useAuthInfo();

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={profileCard} data-open={open || undefined}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={profileTrigger}
            aria-label="Profile menu"
          >
            <MyAvatar size="small" className={profileAvatar} />
            <span className={profileText}>
              <span className={profileName}>{fullName}</span>
              <span className={profileHandle}>@{username}</span>
            </span>
            <NavArrowDownIcon className={profileChevron} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent duration={100}>
          <div className={profileDivider} />
          <ProfileActionsV2 />
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export const ProfileMenuV2 = () => {
  const { authenticated } = useAppLoader();

  if (!authenticated) {
    return <GuestProfileMenuV2 />;
  }

  return <AuthenticatedProfileMenuV2 />;
};
